<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, watch, computed, inject, onMounted } from "vue"
import { useForm } from "vee-validate"
import { useI18n } from "vue-i18n"
import { useDebounceFn } from "@vueuse/core"
import * as yup from "yup"
import type { Address, Building } from "@types"
import type { Services } from "@services"
import { ServiceError } from "@core/exceptions"
import { useFetchWrapper } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            buildingGroupForm: {
                step1Title: "Où se situe ce bâtiment ?",
                step2Title: "Quel nom voulez-vous donner à ce bâtiment ?",
                step3Title: "Quels bâtiments souhaitez-vous associer ?",
                addressPlaceholder: "Adresse du groupe",
                buildingNamePlaceholder: "Nom du bâtiment",
                searchPlaceholder: "Rechercher des bâtiments à associer…",
                validate: "Valider",
                cancel: "Annuler",
                removeChild: "Retirer ce bâtiment du groupe",
                addressFieldLabel: "Adresse",
                errorGroup:
                    "Impossible de créer le groupe ou d'associer les bâtiments. Veuillez réessayer.",
                errorUnknown:
                    "Une erreur inattendue s'est produite. Veuillez réessayer.",
                validation: {
                    addressRequired: "L'adresse est obligatoire",
                    buildingNameRequired: "Le nom du bâtiment est obligatoire",
                    minChildren:
                        "Vous devez associer au moins un bâtiment (immeuble ou maison).",
                },
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
/**
 * Option d'autocomplete avec libellé affiché
 */
type BuildingSearchOption = Building & { label: string }

interface Emits {
    (e: "done", payload: { _id_building: string }): void
    (e: "cancel"): void
}

/**
 * Valeurs validées passées à la soumission (alignées sur le schéma yup).
 */
type BuildingGroupFormValues = {
    address: Address | null
    buildingName: string
    childIds: string[]
}

// ================================
// PROPS & EMITS
// ================================
/**
 * Bâtiments déjà associés à l’ouverture (ex. Storybook : exclusion dans la recherche).
 */
const props = withDefaults(
    defineProps<{
        initialAssociatedBuildings?: Building[]
    }>(),
    {
        initialAssociatedBuildings: () => [],
    }
)

const emit = defineEmits<Emits>()

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")!

// ================================
// VALIDATION (FORMULAIRE)
// ================================
const schema = yup.object({
    address: yup
        .mixed<Address>()
        .required(t("buildingGroupForm.validation.addressRequired"))
        .test(
            "address-non-empty",
            t("buildingGroupForm.validation.addressRequired"),
            (val) => {
                if (!val || typeof val !== "object") return false
                const a = val as Address
                return Boolean(
                    (a.street && a.street.trim()) ||
                        (a.city && a.city.trim()) ||
                        (a.zipcode && a.zipcode.trim())
                )
            }
        ),
    buildingName: yup
        .string()
        .trim()
        .required(t("buildingGroupForm.validation.buildingNameRequired")),
    childIds: yup
        .array()
        .of(yup.string().required())
        .min(1, t("buildingGroupForm.validation.minChildren")),
})

const {
    errors,
    handleSubmit,
    defineField,
    setFieldValue,
    submitCount,
    validateField,
    resetForm,
} = useForm({
    validationSchema: schema,
    validateOnMount: false,
    initialValues: {
        address: null as Address | null,
        buildingName: "",
        childIds: [] as string[],
    },
})

const [addressField] = defineField("address")
const [buildingNameField] = defineField("buildingName")

// ================================
// ÉTAT DES DONNÉES
// ================================
/**
 * Bâtiments déjà associés au futur groupe
 */
const children = ref<Building[]>([])

onMounted(() => {
    if (props.initialAssociatedBuildings.length > 0) {
        children.value = props.initialAssociatedBuildings.map((b) => ({ ...b }))
    }
})

/**
 * Sélection courante dans l'autocomplete de recherche
 */
const selectedSearchBuilding = ref<BuildingSearchOption | null>(null)

/**
 * Résultats bruts de recherche (avant format label)
 */
const results = ref<Building[]>([])

/**
 * Soumission : création du groupe + enfants (états via useFetchWrapper).
 */
const {
    fetch: submitBuildingGroup,
    isFetching: isSubmittingGroup,
    hasError: hasSubmitError,
    error: submitError,
} = useFetchWrapper(async (values: BuildingGroupFormValues) => {
    const created = await services.$createBuilding(
        (values.buildingName ?? "").trim(),
        values.address as Address,
        { level: "groupe" }
    )

    const buildingId = String(created._id ?? created._id_building ?? "")
    if (!buildingId) {
        throw new Error("Missing building id")
    }

    if (children.value.length > 0) {
        await services.$addBuildingChildren(
            buildingId,
            children.value.map((c) => c._id)
        )
    }

    emit("done", { _id_building: buildingId })
    resetFormAfterSuccess()
})

/**
 * Remet le formulaire à l’état initial après création réussie (y compris bâtiments associés initiaux).
 */
function resetFormAfterSuccess() {
    resetForm({
        values: {
            address: null as Address | null,
            buildingName: "",
            childIds: [] as string[],
        },
    })
    children.value =
        props.initialAssociatedBuildings.length > 0
            ? props.initialAssociatedBuildings.map((b) => ({ ...b }))
            : []
    selectedSearchBuilding.value = null
    results.value = []
}

/**
 * Message d’erreur API soumission (hors validation champ)
 */
const getSubmitErrorMessage = computed(() => {
    const err = submitError.value
    if (!err) return ""
    if (err instanceof ServiceError) {
        return t("buildingGroupForm.errorGroup")
    }
    return t("buildingGroupForm.errorUnknown")
})

/**
 * Affiche l’alerte d’erreur API après une soumission échouée
 */
const getShowSubmitErrorAlert = computed(
    () => hasSubmitError.value && !isSubmittingGroup.value
)

/**
 * Erreur champ enfants : comme l'adresse, affichée après une tentative de validation du formulaire
 */
const getShouldShowChildIdsError = computed(() => {
    if (!errors.value.childIds) return false
    return submitCount.value > 0
})
const getShouldShowBuildingNameError = computed(() => {
    if (!errors.value.buildingName) return false
    return submitCount.value > 0
})

// ================================
// RECHERCHE DE BÂTIMENTS
// ================================
const {
    fetch: fetchBuildings,
    isFetching: isSearching,
    hasError: hasErrorSearch,
    error: errorSearch,
} = useFetchWrapper(async (query: string) => {
    const trimmed = query.trim()
    if (!trimmed || trimmed.length < 2) {
        results.value = []
        return
    }

    let buildings = await services.$searchBuildings(trimmed)

    buildings = buildings.filter((b) => {
        const level = b.infos?.niveau
        return level === "immeuble" || level === "maison"
    })

    const childIds = new Set(children.value.map((c) => c._id))
    buildings = buildings.filter((b) => !childIds.has(b._id))

    results.value = Array.isArray(buildings) ? buildings : []
})

const searchBuildingsDebounced = useDebounceFn((event: { query: string }) => {
    fetchBuildings(event.query).catch(() => {
        results.value = []
    })
}, 300)

/**
 * Suggestions avec libellé pour BaseAutoComplete
 */
const getFormattedSearchResults = computed((): BuildingSearchOption[] => {
    if (!Array.isArray(results.value)) return []
    return results.value.map((building) => ({
        ...building,
        label: formatBuildingRowLabel(building),
    }))
})

/**
 * Marge droite de l’input pendant le chargement pour ne pas passer sous le loader (comme InputCompany).
 */
const getBuildingSearchInputTrailingClass = computed(() =>
    isSearching.value ? "pr-10" : ""
)

// ================================
// LOGIQUE MÉTIER
// ================================
/**
 * Formate l'adresse d'un bâtiment sur une ligne
 * @param address - Adresse métier
 * @returns Texte affichable
 */
function formatAddressLine(address: Building["address"] | undefined): string {
    if (!address) return ""
    const parts = [address.street, address.zipcode, address.city].filter(
        Boolean
    )
    return parts.join(", ")
}

/**
 * Libellé pour une ligne de résultat ou liste d'enfants
 * @param building - Bâtiment source
 * @returns Chaîne affichée
 */
function formatBuildingRowLabel(building: Building): string {
    const addr = formatAddressLine(building.address)
    if (building.name?.trim()) {
        return addr ? `${building.name} — ${addr}` : building.name
    }
    return addr || "—"
}

/**
 * Type d'icône métier pour BaseBuildingIcon
 * @param niveau - Valeur renvoyée par l'API (infos.niveau)
 * @returns Clé reconnue par BaseBuildingIcon
 */
function getIconTypeForLevel(niveau: string | undefined): string {
    if (
        niveau === "maison" ||
        niveau === "immeuble" ||
        niveau === "groupe" ||
        niveau === "appartement"
    ) {
        return niveau
    }
    return "immeuble"
}

// ================================
// SYNCHRONISATION CHAMPS / ENFANTS
// ================================
watch(
    children,
    async (list) => {
        setFieldValue(
            "childIds",
            list.map((b) => b._id),
            false
        )
        if (submitCount.value > 0) {
            await validateField("childIds")
        }
    },
    { deep: true }
)

watch(selectedSearchBuilding, (picked) => {
    if (!picked?._id) return
    if (children.value.some((c) => c._id === picked._id)) {
        selectedSearchBuilding.value = null
        return
    }
    children.value = [...children.value, picked]
    selectedSearchBuilding.value = null
    results.value = []
})

// ================================
// INTERACTIONS UTILISATEUR
// ================================
/**
 * Retire un bâtiment associé
 * @param childId - Identifiant du bâtiment enfant
 */
function onRemoveChild(childId: string) {
    children.value = children.value.filter((c) => c._id !== childId)
}

/**
 * Soumission : validation puis appel API encapsulé dans useFetchWrapper
 */
const onSubmit = handleSubmit(async (values) => {
    if (isSubmittingGroup.value) return
    await submitBuildingGroup(values as BuildingGroupFormValues)
})

/**
 * Annulation : événement au parent (bloquée pendant la soumission)
 */
function onCancel() {
    if (isSubmittingGroup.value) return
    emit("cancel")
}
</script>

<template>
    <form
        class="building-group-form flex flex-col gap-6"
        @submit.prevent="onSubmit"
    >
        <!-- ================================ -->
        <!-- NOM DU GROUPE                 -->
        <!-- ================================ -->
        <div class="building-group-form__section w-full">
            <h3 class="text-base font-medium text-surface-800 mb-2">
                {{ t("buildingGroupForm.step2Title") }}
            </h3>
            <BaseInputWrapper
                input-id="building-group-name"
                :error="
                    getShouldShowBuildingNameError
                        ? errors.buildingName
                        : undefined
                "
                unfloat
            >
                <BaseInputText
                    id="building-group-name"
                    v-model="buildingNameField"
                    :placeholder="
                        t('buildingGroupForm.buildingNamePlaceholder')
                    "
                    :invalid="getShouldShowBuildingNameError"
                    :readonly="isSubmittingGroup"
                    class="w-full"
                    data-cy="input-building-name"
                />
            </BaseInputWrapper>
        </div>

        <!-- ================================ -->
        <!-- ADRESSE DU GROUPE -->
        <!-- ================================ -->
        <div class="building-group-form__section w-full">
            <h3
                class="flex items-center gap-2 text-base font-medium text-surface-800 mb-2"
            >
                <BaseBuildingIcon type="groupe" :size="28" />
                {{ t("buildingGroupForm.step1Title") }}
            </h3>
            <InputAddress
                v-model="addressField"
                input-id="building-group-address"
                :label="t('buildingGroupForm.addressFieldLabel')"
                :placeholder="t('buildingGroupForm.addressPlaceholder')"
                :error="errors.address"
                :readonly="isSubmittingGroup"
                data-cy="input-address"
            />
        </div>

        <!-- ================================ -->
        <!-- ASSOCIATION DES BÂTIMENTS -->
        <!-- ================================ -->
        <div class="building-group-form__section w-full">
            <h3
                class="flex items-center gap-2 text-base font-medium text-surface-800 mb-2"
            >
                <BaseBuildingIcon type="maison_appartement" :size="28" />
                {{ t("buildingGroupForm.step3Title") }}
            </h3>
            <div class="space-y-3">
                <div
                    v-if="children.length > 0"
                    class="flex flex-col divide-y divide-surface-200 rounded-md border border-surface-200"
                >
                    <div
                        v-for="child in children"
                        :key="child._id"
                        class="flex items-start gap-3 p-3"
                    >
                        <BaseBuildingIcon
                            :type="getIconTypeForLevel(child.infos?.niveau)"
                            :size="40"
                        />
                        <div class="min-w-0 flex-1">
                            <p class="truncate font-medium text-surface-900">
                                {{ child.name?.trim() ? child.name : "—" }}
                            </p>
                            <p class="text-xs text-surface-600">
                                {{ formatAddressLine(child.address) }}
                            </p>
                        </div>
                        <BaseButton
                            type="button"
                            severity="danger"
                            outlined
                            icon="pi pi-trash"
                            :disabled="isSubmittingGroup"
                            :aria-label="t('buildingGroupForm.removeChild')"
                            @click="onRemoveChild(child._id)"
                        />
                    </div>
                </div>

                <BaseInputWrapper
                    input-id="building-group-search"
                    :error="
                        getShouldShowChildIdsError ? errors.childIds : undefined
                    "
                    unfloat
                >
                    <div class="relative w-full">
                        <BaseAutoComplete
                            v-model="selectedSearchBuilding"
                            :suggestions="getFormattedSearchResults"
                            :loading="isSearching"
                            :disabled="isSubmittingGroup"
                            :placeholder="
                                t('buildingGroupForm.searchPlaceholder')
                            "
                            option-label="label"
                            data-key="_id"
                            :invalid="getShouldShowChildIdsError"
                            show-clear
                            fluid
                            :pt="{
                                root: 'w-full!',
                                loader: {
                                    class: 'z-[2]',
                                },
                                pcInputText: {
                                    root: {
                                        class: getBuildingSearchInputTrailingClass,
                                    },
                                },
                            }"
                            @complete="searchBuildingsDebounced"
                        >
                            <template #option="slotProps">
                                <span>{{ slotProps.option.label }}</span>
                            </template>
                        </BaseAutoComplete>
                    </div>
                </BaseInputWrapper>

                <BaseMessage
                    v-if="hasErrorSearch"
                    severity="error"
                    variant="simple"
                    size="small"
                >
                    <p>{{ errorSearch?.message }}</p>
                </BaseMessage>
            </div>
        </div>

        <!-- ================================ -->
        <!-- ACTIONS -->
        <!-- ================================ -->
        <div class="flex w-full flex-wrap items-center justify-center gap-3">
            <BaseButton
                type="submit"
                :label="t('buildingGroupForm.validate')"
                :loading="isSubmittingGroup"
                :disabled="isSubmittingGroup"
            />
            <BaseButton
                type="button"
                severity="secondary"
                :label="t('buildingGroupForm.cancel')"
                :disabled="isSubmittingGroup"
                @click="onCancel"
            />
        </div>

        <!-- ================================ -->
        <!-- RETOURS API -->
        <!-- ================================ -->
        <BaseMessage
            v-if="getShowSubmitErrorAlert"
            severity="error"
            variant="simple"
            class="w-full"
        >
            <p>{{ getSubmitErrorMessage }}</p>
        </BaseMessage>
    </form>
</template>
